import MarkerType from "../enums/markerType";
import SitePlanType from "../enums/sitePlanType";

const toLatLng = (item) => ({
    lat: +item.Latitude,
    lng: +item.Longitude,
  });

const Z_INDEX_BY_TYPE = {
  [MarkerType.PlanUnit.toString()]: 2,
  [MarkerType.SpecUnit.toString()]: 3,
  [MarkerType.HousingDevelopment.toString()]: 4,
  [MarkerType.BuildablePlan.toString()]: 4,
};

const getMarkerZIndex = (marker) => 
  // Basic markers are intentionally deprioritised to z-index 1, below all type-based z-indices.
   marker?.isBasic ? 1 : (Z_INDEX_BY_TYPE[marker?.type] ?? 0)

/**
 * Maps fields shared by all four marker types.
 * Each specific mapper spreads this and adds its type-specific fields.
 */
const mapBaseListingFields = (raw) => ({
  position: toLatLng(raw),
  id: raw.Id,
  communityId: raw.CommunityId,
  name: raw.Name,
  title: raw.Name,
  isAdult: raw.IsAdult,
  isBasic: raw.IsBasic,
  sitePlanType: raw.SitePlanType,
  url: raw.Url,
  latitude: raw.Latitude,
  longitude: raw.Longitude,
  priceText: raw.PriceText,
  previousPrice: raw.PreviousPrice,
  priceReducedTrackingLabel: raw.PriceReducedTrackingLabel,
  isCallForPrice: raw.IsCallForPrice === true,
  address: raw.Address,
  averageRating: raw.AverageRating,
  bedrooms: raw.Bedrooms,
  bathrooms: raw.Bathrooms,
  garages: raw.Garages,
  squareFeetRange: raw.SquareFeetRange,
  brandName: raw.BrandName,
  stateAbbreviation: raw.StateAbbreviation,
  stateName: raw.StateName,
  zip: raw.Zip,
  communityName: raw.CommunityName,
  marketName: raw.MarketName,
  city: raw.City,
  planTypeName: raw.PlanTypeName,
  hasSelfGuidedTour: raw.HasSelfGuidedTour === true,
  isHotDeal: raw.IsHotDeal === true,
  thumbnail: raw.Thumbnail,
  shortPrice: raw.ShortPrice,
  communityStatus: raw.CommunityStatus,
});

/**
 * Maps fields shared by community-shaped markers (HousingDevelopment + BuildablePlan).
 * Both share communityId === Id and a set of community-only fields.
 */
const mapBaseCommunityFields = (raw) => ({
  ...mapBaseListingFields(raw),
  communityId: raw.Id,
  builderId: raw.BuilderId,
  isMasterPlannedCommunity: raw.IsMasterPlannedCommunity === true,
  priceReducedText: raw.PriceReducedText,
  titleText: raw.Title,
  page: raw.Page,
  priceHigh: raw.PriceHigh,
  priceLow: raw.PriceLow,
  totalPlans: raw.TotalPlans,
  builderCount: raw.BuilderCount,
  communityCount: raw.CommunityCount,
});

export const mapSpecUnitMarker = (spec) => ({
  ...mapBaseListingFields(spec),
  type: MarkerType.SpecUnit.toString(),
  displayText: spec.ShortPrice,
  zIndex: getMarkerZIndex({ type: MarkerType.SpecUnit.toString(), isBasic: spec.IsBasic }),
  price: spec.Price,
  isModelHome: spec.IsModelHome === true,
});

export const mapPlanUnitMarker = (plan) => ({
  ...mapBaseListingFields(plan),
  type: MarkerType.PlanUnit.toString(),
  displayText: plan.ShortPrice,
  zIndex: getMarkerZIndex({ type: MarkerType.PlanUnit.toString(), isBasic: plan.IsBasic }),
  price: plan.Price,
});

export const mapCommunityMarker = (community) => ({
  ...mapBaseCommunityFields(community),
  type: MarkerType.HousingDevelopment.toString(),
  displayText: community.ShortPrice,
  zIndex: getMarkerZIndex({ type: MarkerType.HousingDevelopment.toString(), isBasic: community.IsBasic }),
  hasInteractiveSiteMap: community.SitePlanType === SitePlanType.Interactive.toString(),
});

export const mapBuildablePlanMarker = (community) => ({
  ...mapBaseCommunityFields(community),
  type: MarkerType.BuildablePlan.toString(),
  displayText: `${community.TotalPlans} PLANS`,
  zIndex: getMarkerZIndex({ type: MarkerType.BuildablePlan.toString(), isBasic: community.IsBasic }),
});

/**
 * Converts a marker object (from the Redux store) to the information shape that
 * standardCard `setInformation()` methods expect (all camelCase properties).
 *
 * @param {Object} marker - Marker instance from `selectMapResults` selector.
 * @returns {Object} Information object ready for `cardInstance.setInformation()`.
 */
export function mapMarkerToCardInformation(marker) {
  return {
    bathrooms: marker.bathrooms,
    bedrooms: marker.bedrooms,
    brandName: marker.brandName,
    builderId: marker.builderId,
    builderCount: marker.builderCount,
    builderRating: marker.averageRating,
    communityCount: marker.communityCount,
    communityId: marker.communityId,
    communityName: marker.communityName,
    marketingStatus: marker.communityStatus,
    city: marker.city,
    formattedBathrooms: marker.bathrooms,
    formattedBedrooms: marker.bedrooms,
    formattedGarages: marker.garages,
    formattedSquareFeet: marker.squareFeet,
    garages: marker.garages,
    hasInteractiveSiteMap: !!marker.hasInteractiveSiteMap,
    id: marker.id,
    isAdult: !!marker.isAdult,
    isBasic: !!marker.isBasic,
    isMasterPlannedCommunity: !!marker.isMasterPlannedCommunity,
    isModelHome: !!marker.isModelHome,
    latitude: marker.position?.lat,
    longitude: marker.position?.lng,
    name: marker.name || marker.title,
    price: marker.priceText,
    previousPrice: marker.previousPrice,
    priceHigh: marker.priceHigh,
    priceLow: marker.priceLow,
    priceReduced: marker.priceReducedText,
    squareFeet: marker.squareFeet,
    stateAbbreviation: marker.stateAbbreviation,
    thumbnail: marker.thumbnail,
    totalPlans: marker.totalPlans,
    type: marker.type,
    url: marker.url,
    wasMatchedAsSpotlightResult: !!marker.wasMatchedAsSpotlightResult,
    zip: marker.zip,
  };
}